/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { EventEmitter } from "events";
import { performance } from "perf_hooks";

import { Context } from "@opentelemetry/api";
import { ExportResultCode } from "@opentelemetry/core";
import { Span, SpanProcessor } from "@opentelemetry/sdk-trace-base";

import { CTIds, ExportType } from "../Types";
import { ComponentLogger } from "../Logger";
import { getConfiguration } from "../configuration/StartupConfig";
import { setTimeoutUnref } from "../utils/Timers";
import { checkNumericOption, checkBooleanOption } from "../utils/Validators";
import { DtSpanExporter, ResultCallback } from "./DtSpanExporter";
import { getMetaData } from "./SpanEmbedder";
import { PrepareResult, SendState, TransmitOptions } from "./SpanMetaData";
import { prepareSpanForExport, addStacktrace } from "./SpanUpdater";
import * as SemConvTrace from "../../gen/dynatrace/odin/semconv/v1/SemConvTrace";

// ============================================================================

const cDefaultUpdateIntervalMs = 3_000;
const cDefaultKeepAliveTimeoutMs = 25_000;
const cDefaultExportSpanCntMax = 200;
const cDefaultOpenSpanTimeoutMs = 115 * 60 * 1000;
const cDefaultMaxSpans = 5_000;
const cDefaultForceFlushTimeoutMs = 5_000;
const cDefaultMinimumExportTimeMs = 500;
const cDefaultAddStackOnStart = false;

// ----------------------------------------------------------------------------
export interface DtSpanProcessorConfig {
	clusterId?: number;
	tenantId?: number;
	updateIntervalMs?: number;
	keepAliveIntervalMs?: number;
	exportSpanCntMax?: number;
	openSpanTimeoutMs?: number;
	forceFlushTimeoutMs?: number;
	maxSpans?: number;
	minimumExportTimeMs?: number;
	debugAddStackOnStart?: boolean;
}
// ----------------------------------------------------------------------------
export class DtSpanProcessor extends EventEmitter implements SpanProcessor {
	constructor(exporter: DtSpanExporter, config?: Readonly<DtSpanProcessorConfig>) {
		super();
		if (!(exporter instanceof DtSpanExporter)) {
			this._logger.error("exporter must be an instance of DtSpanExporter");
			throw new TypeError("DtSpanProcessor: exporter must be an instance of DtSpanExporter");
		}
		config = { ...getConfiguration(), ...config };
		if (config == null || typeof(config) !== "object") {
			throw new TypeError("DtSpanProcessor: config invalid");
		}
		if (typeof(config.clusterId) !== "number") {
			throw new TypeError("DtSpanProcessor: clusterId must be a number");
		}
		if (typeof(config.tenantId) !== "number") {
			throw new TypeError("DtSpanProcessor: tenantId must be a number");
		}
		this._updateIntervalMs = checkNumericOption("DtSpanProcessor", config, "updateIntervalMs", cDefaultUpdateIntervalMs);
		this._keepAliveIntervalMs = checkNumericOption("DtSpanProcessor", config, "keepAliveIntervalMs", cDefaultKeepAliveTimeoutMs);
		this._exportSpanCntMax = checkNumericOption("DtSpanProcessor", config, "exportSpanCntMax", cDefaultExportSpanCntMax);
		this._openSpanTimeoutMs = checkNumericOption("DtSpanProcessor", config, "openSpanTimeoutMs", cDefaultOpenSpanTimeoutMs);
		this._forceFlushTimeoutMs = checkNumericOption("DtSpanProcessor", config, "forceFlushTimeoutMs", cDefaultForceFlushTimeoutMs);
		this._maxSpans = checkNumericOption("DtSpanProcessor", config, "maxSpans", cDefaultMaxSpans);
		this._minimumExportTimeMs = checkNumericOption("DtSpanProcessor", config, "minimumExportTimeMs", cDefaultMinimumExportTimeMs);
		this._addStackOnStart = checkBooleanOption("DtSpanProcessor", config, "debugAddStackOnStart", cDefaultAddStackOnStart);

		this._exporter = exporter;
		this._transmitOptions = {
			keepAliveIntervalMs: this._keepAliveIntervalMs,
			openSpanTimeoutMs: this._openSpanTimeoutMs,
			updateIntervalMs: this._updateIntervalMs
		};
		this._ids = {
			clusterId: config.clusterId,
			tenantId: config.tenantId
		};
	}

	public onStart(span: Span, parentContext: Context): void {
		if (this._isShutdown) {
			return;
		}

		if (this._addStackOnStart) {
			addStacktrace(SemConvTrace.DT_STACKTRACE_ONSTART, span, this._logger);
		}

		prepareSpanForExport(span, parentContext, this._ids, this._transmitOptions, this._logger);

		this._addSpan(span);
	}

	public onEnd(span: Span): void {
		if (this._isShutdown) {
			return;
		}

		if (!this._spans.has(span)) {
			if (this._logger.debugEnabled) {
				this._logger.debug(`onEnd add span ${span.spanContext().spanId}`);
			}
			this._addSpan(span);
		}
	}

	public forceFlush(): Promise<void> {
		if (this._logger.debugEnabled) {
			this._logger.debug(`forceFlush ${this._stateToString()}`);
		}

		if (this._isShutdown) {
			return Promise.resolve();
		}

		if (this._forceFlushPromise != null) {
			return this._forceFlushPromise;
		}

		this._forceFlushPromise = new Promise((resolve) => {
			const done = () => {
				this._forceFlushPromise = undefined;
				resolve();
			};
			if (this._isExporting) {
				this._exportAfterWait(done);
			} else {
				this._export(ExportType.ForceFlush, done);
			}
		});
		return this._forceFlushPromise;
	}

	public shutdown(): Promise<void> {
		this._logger.info(`shutdown ${this._isShutdown}`);

		if (this._isShutdown) {
			return Promise.resolve();
		}

		const p = this.forceFlush();
		this._isShutdown = true;
		return p;
	}

	private _exportAfterWait(done: () => void): void {
		const start = performance.now();
		const exportAfterWaiting = () => {
			const timeRemaining = this._forceFlushTimeoutMs - (performance.now() - start);
			if (this._logger.debugEnabled) {
				this._logger.debug(`exportAfterWaiting ${this._stateToString()}, timeRemaining:${timeRemaining}`);
			}

			if (this._forceFlushTimer) {
				clearTimeout(this._forceFlushTimer);
				this._forceFlushTimer = undefined;
			}
			this.removeListener("export:done", exportAfterWaiting);

			// if flush cannot be completed in the time remaining after waiting, abort
			if (timeRemaining <= this._minimumExportTimeMs) {
				done();
			} else {
				process.nextTick(this._export, ExportType.ForceFlush, done);
				const flushTimeout = setTimeoutUnref(this._abort, timeRemaining);
				this.once("export:done", () => clearTimeout(flushTimeout));
			}
		};

		this.once("export:done", exportAfterWaiting);
		this._forceFlushTimer = setTimeoutUnref(() => {
			// timeout has passed and export is not finished.
			// abort currently running export.
			this._abort();
			this.removeListener("export:done", exportAfterWaiting);
			this._forceFlushTimer = undefined;
			done();
		}, this._forceFlushTimeoutMs);
	}

	private _addSpan(span: Span): void {
		if (this._spans.size >= this._maxSpans) {
			if (!this._maxSpansLogged) {
				this._logger.warn("skip span as maxSpans limit is reached");
			}
			return;
		}
		this._maxSpansLogged = false;

		this._spans.add(span);
		if (!this._isExporting && (this._spans.size === 1)) {
			this._startUnrefedTimer();
		}
	}

	private _startUnrefedTimer(): void {
		if (this._logger.debugEnabled) {
			this._logger.debug(`startTimer ${this._stateToString()}`);
		}
		setTimeoutUnref(this._export, this._updateIntervalMs);
	}

	private readonly _export = (type = ExportType.Normal, callback = () => { /**/ }): void => {
		const debugEnabled = this._logger.debugEnabled;
		if (debugEnabled) {
			this._logger.debug(`_export ${this._stateToString()}`);
		}

		// avoid parallel exporting
		if (this._isExporting) {
			process.nextTick(callback);
			return;
		}

		if (this._exportIterator == null) {
			// previous export is finished
			if (this._spans.size === 0) {
				// nothing left to do
				process.nextTick(callback);
				return;
			}
			// start exporting by creating a new iterator (iterates in insertion order)
			this._exportIterator = this._spans.values();
		}

		// take a bunch up to max size
		const now = performance.now();
		let rc = this._exportIterator.next();
		while (!rc.done) {
			const span = rc.value;
			switch (getMetaData(span)!.prepareSend(now, span.ended)) {
				case PrepareResult.Drop:
					this._spans.delete(span);
					break;
				case PrepareResult.Send:
					this._exportingSpans.push(span);
					break;
				case PrepareResult.Skip:
					break;
			}
			if (this._exportingSpans.length >= this._exportSpanCntMax) {
				break;
			}
			rc = this._exportIterator.next();
		}

		if (rc.done) {
			this._exportIterator = undefined;
		}

		if (debugEnabled) {
			this._logger.debug(`export ${this._stateToString()}`);
		}
		this._exporter.export(this._exportingSpans, this._onExportDone(type, callback), type);
		this._isExporting = true;
	};

	private _onExportDone(type: ExportType, callback: () => void): ResultCallback {
		return (result) => {
			if (this._logger.debugEnabled) {
				this._logger.debug(`exportDone type: ${ExportType[type]}, result: ${ExportResultCode[result.code]}, ${this._stateToString()}`);
			}

			this._isExporting = false;

			// remove finished spans even on failed exports as retry handling is in exporter
			for (const span of this._exportingSpans) {
				const metaData = getMetaData(span)!;
				if (metaData.sendState === SendState.Finished) {
					this._spans.delete(span);
				}
			}
			this._exportingSpans.length = 0;

			// export next chunk or start timer
			if (this._exportIterator != null) {
				process.nextTick(this._export, type, callback);
			} else {
				process.nextTick(callback);
				this.emit("export:done");
				if (this._spans.size > 0) {
					this._startUnrefedTimer();
				}
			}
		};
	}

	private _abort = () => {
		if (this._logger.debugEnabled) {
			this._logger.debug(`abort ${this._stateToString()}`);
		}
		this._exporter.abort();
		this._exportIterator = undefined;
	};

	private _stateToString(): string {
		return `spanCnt: ${this._spans.size}, exportingSpanCnt: ${this._exportingSpans.length}, exportIterator:${this._exportIterator != null}` +
			`, isExporting:${this._isExporting}, forceFlushTimer:${this._forceFlushTimer != null}`;
	}

	private _isShutdown = false;

	private _spans: Set<Span> = new Set();
	private _exportingSpans: Span[] = [];
	private _exportIterator?: IterableIterator<Span>;
	private _isExporting = false;
	private _forceFlushTimer: NodeJS.Timeout | undefined;
	private _forceFlushPromise: Promise<void> | undefined;
	private _maxSpansLogged = false;

	private readonly _logger = new ComponentLogger("DtSpanProcessor");
	private readonly _exporter: DtSpanExporter;
	private readonly _exportSpanCntMax: number;
	private readonly _updateIntervalMs: number;
	private readonly _keepAliveIntervalMs: number;
	private readonly _openSpanTimeoutMs: number;
	private readonly _forceFlushTimeoutMs: number;
	private readonly _maxSpans: number;
	private readonly _minimumExportTimeMs: number;
	private readonly _transmitOptions: TransmitOptions;
	private readonly _ids: CTIds;
	private readonly _addStackOnStart: boolean;
}
